<!--
    Single platform-aware icon accessor. Globally registered (see main.js /
    demo.swingu.js / swingu.js), so use it anywhere without importing:

        <Icon name="share" />                        resolves the ios/android variant automatically
        <Icon name="close" class="text-gray-500 w-[15px] h-[15px]" /> shared icon, colored via Tailwind
        <Icon name="golfer" class="h-[40px]" /> size with a class; the other axis auto-scales
        <Icon name="share" platform="ios" />         force a platform

    Add a new icon by dropping a .svg (using currentColor) into ./ios, ./android,
    or ./shared — a component is generated under .generated/ on dev/build. Drop a
    .vue there instead for icons that need bespoke props (multi-color, etc.).

    Resolution order: explicit `platform` prop -> auto-detected platform -> `shared` fallback.
-->
<template>
    <component
        :is="resolved"
        v-if="resolved"
        v-bind="$attrs"
        :class="defaultSizeClass"
    />
</template>

<script lang="ts">
import type { Component } from "vue";

type IconLoader = () => Promise<{ default: Component }>;

// Hot list: icons used on nearly every screen are bundled EAGERLY, so they render
// with no extra request and no pop-in. Keep this list SHORT — only icons that are
// used almost everywhere (chosen from codebase usage counts). Everything else stays
// lazy below. These are all shared (no platform variants).
const eagerModules = import.meta.glob<{ default: Component }>(
    [
        "./shared/.generated/close.vue",
        "./shared/.generated/caret-down.vue",
        "./shared/.generated/info.vue",
        "./shared/.generated/lock-filled.vue",
        "./shared/.generated/target-average.vue",
        "./shared/.generated/player-average.vue",
    ],
    { eager: true }
);

// The remaining icons as lazy loaders: one chunk per icon, fetched on first
// render, so adding icons never bloats the chunks that render <Icon>. Includes
// both hand-written .vue and the .generated/ components built from .svg sources.
// The eager hot-list above is excluded so it isn't also emitted as (never-fetched)
// chunks.
const lazyModules = import.meta.glob<{ default: Component }>([
    "./ios/*.vue",
    "./android/*.vue",
    "./shared/*.vue",
    "./ios/.generated/*.vue",
    "./android/.generated/*.vue",
    "./shared/.generated/*.vue",
    "!./shared/.generated/close.vue",
    "!./shared/.generated/caret-down.vue",
    "!./shared/.generated/info.vue",
    "!./shared/.generated/lock-filled.vue",
    "!./shared/.generated/target-average.vue",
    "!./shared/.generated/player-average.vue",
]);

// Tracks .svg-built (generated) icons, which carry no width/height and so take the
// injected default size; hand-written icons manage their own dimensions. Populated
// from both the eager hot list and the lazy registry below.
const generatedNames = new Set<string>();

// name -> already-resolved component (eager hot list; shared only). Handles both
// hand-written `./shared/<name>.vue` and generated `./shared/.generated/<name>.vue`.
const eagerRegistry: Record<string, Component> = {};
for (const [filePath, module] of Object.entries(eagerModules)) {
    const match = filePath.match(/\.\/shared\/(\.generated\/)?(.+)\.vue$/);
    if (match) {
        eagerRegistry[match[2]] = module.default;
        if (match[1]) {
            generatedNames.add(match[2]);
        }
    }
}

// platform -> name -> lazy loader (a name lives in either the folder root, as a
// hand-written .vue, or in .generated/, built from a .svg — never both).
const lazyRegistry: Record<string, Record<string, IconLoader>> = {};
for (const [filePath, loader] of Object.entries(lazyModules)) {
    const match = filePath.match(
        /\.\/(ios|android|shared)\/(\.generated\/)?(.+)\.vue$/
    );
    if (match) {
        (lazyRegistry[match[1]] ??= {})[match[3]] = loader;
        if (match[2]) {
            generatedNames.add(match[3]);
        }
    }
}

// Loaded lazy icons are cached by `folder/name` so each chunk is fetched once and
// shared across every <Icon> instance.
const componentCache = new Map<string, Component>();

type Resolution =
    | { component: Component } // eager / already-loaded: render synchronously
    | { key: string; loader: IconLoader } // lazy: fetch then cache
    | null;

/**
 * Resolve an icon for a platform. Order: platform-specific variant (lazy) ->
 * eagerly-bundled shared icon (no request) -> lazy shared icon. Null if none.
 * Lazy icons are returned as a loader rather than a Vue async component on purpose
 * — see the loading note in <script setup>.
 */
function resolveIcon(platform: string, name: string): Resolution {
    if (platform !== "shared" && lazyRegistry[platform]?.[name]) {
        const key = `${platform}/${name}`;
        return (
            (componentCache.has(key) && {
                component: componentCache.get(key)!,
            }) || { key, loader: lazyRegistry[platform][name] }
        );
    }
    if (eagerRegistry[name]) {
        return { component: eagerRegistry[name] };
    }
    if (lazyRegistry.shared?.[name]) {
        const key = `shared/${name}`;
        return (
            (componentCache.has(key) && {
                component: componentCache.get(key)!,
            }) || { key, loader: lazyRegistry.shared[name] }
        );
    }
    return null;
}

export default {
    // Forward class/style/aria-* straight to the underlying <svg>, not a wrapper.
    inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed, markRaw, shallowRef, useAttrs, watch } from "vue";
import { isAndroid } from "@global/composables/webview-events";
// Not @sentry/vue directly: <Icon> is registered globally, so a static import would
// land the SDK in swingu.js and demo.swingu.js too, which never initialise Sentry.
import { reportError } from "@global/composables/report-error";
import type { IconName } from "./icon-names.generated";

// A dropped chunk request is routine on the mobile connections these webviews run
// on, so retry once before giving up.
const RETRY_DELAY_MS = 300;

/**
 * Fetch a lazy icon chunk, retrying once after a short delay.
 *
 * Still rejects if both attempts fail — the caller must catch.
 */
const loadWithRetry = (loader: IconLoader): Promise<{ default: Component }> =>
    loader().catch(
        () =>
            new Promise<{ default: Component }>((resolve, reject) => {
                setTimeout(
                    () => loader().then(resolve, reject),
                    RETRY_DELAY_MS
                );
            })
    );

const props = withDefaults(
    defineProps<{
        name: IconName;
        // "ios" | "android" | null (null = auto-detect the current device).
        platform?: "ios" | "android" | null;
    }>(),
    {
        platform: null,
    }
);

// The resolved icon component. We deliberately DON'T use `defineAsyncComponent`
// for lazy icons: it inserts an AsyncWrapper component into the tree, and when that
// wrapper resolves in a microtask while rendering inside a render-function/functional
// parent (e.g. vue3-carousel's <Carousel>/<Slide>/<Navigation>, which invoke their
// slots during their own render), the wrapper's deferred re-render runs with a null
// `currentRenderingInstance` and throws "Cannot read properties of null (reading
// 'isCE')". Instead we load the chunk ourselves into this ref, so <Icon> stays a
// plain component that re-renders through its own instance. Code-splitting is kept
// (each icon is still its own lazy chunk via import.meta.glob).
const resolved = shallowRef<Component | null>(null);

// Bumped on every (re)resolve so an in-flight lazy load that finishes after the
// `name`/`platform` changed doesn't clobber the newer icon.
let loadToken = 0;

const loadIcon = (): void => {
    const token = ++loadToken;
    // Backend defaults deviceType to "ios", so fall back to ios when not Android.
    const platform = props.platform ?? (isAndroid() ? "android" : "ios");
    const resolution = resolveIcon(platform, props.name);

    if (!resolution) {
        resolved.value = null;
        if (import.meta.env.DEV) {
            console.warn(
                `[Icon] No icon "${props.name}" for platform "${platform}" (and no shared fallback).`
            );
        }
        return;
    }

    if ("component" in resolution) {
        resolved.value = resolution.component;
        return;
    }

    // Read now, not in the handlers below: `props.name` is reactive, so by the time
    // a rejection arrives it may already name a different icon.
    const iconName = props.name;

    // Lazy: clear any previous icon, fetch the chunk, then cache + show it (unless a
    // newer resolve has since superseded this one).
    resolved.value = null;
    loadWithRetry(resolution.loader)
        .then((module) => {
            const component = markRaw(module.default);
            componentCache.set(resolution.key, component);
            if (token === loadToken) {
                resolved.value = component;
            }
        })
        .catch((error) => {
            // A newer name/platform has already superseded this load, so nothing is
            // waiting on the chunk and its failure changed nothing the user can see.
            // Reporting it would just add noise attributed to an icon we no longer
            // want; the rate stays visible through loads that are still current.
            if (token !== loadToken) {
                return;
            }

            // Both attempts failed. Report it, but do not throw: it's better that
            // the icon not load than that the page shows an error. On entrypoints
            // with no reporter registered this is a no-op.
            reportError(error, { tags: { iconName } });
        });
};

watch([() => props.name, () => props.platform], loadIcon, { immediate: true });

// Generated (.svg-built) icons carry no width/height — they're sized purely by
// Tailwind classes, so EITHER `w-*` or `h-*` works (the other axis auto-scales
// from the viewBox). Inject a default size for those, but ONLY when the caller
// passed no width/height utility — so the default never conflicts with a caller
// class, and hand-written icons (which manage their own size) are never touched.
const attrs = useAttrs();
const SIZE_CLASS_RE = /(?:^|\s)(?:size-|w-|h-|min-w-|max-w-|min-h-|max-h-)/;
const defaultSizeClass = computed(() => {
    if (!generatedNames.has(props.name)) {
        return "";
    }
    // Caller already controls the size, via a Tailwind utility class or a
    // width/height attribute — don't impose the fallback.
    const hasSizeClass = SIZE_CLASS_RE.test(String(attrs.class ?? ""));
    const hasSizeAttr = attrs.width != null || attrs.height != null;
    return hasSizeClass || hasSizeAttr ? "" : "h-6";
});
</script>
